// landscapeFullscreen.js
import {
  getIsOnMobile,
  getIsOnIOS,
  getIsHigherAndroidChrome80,
} from '../resource/getUserAgent.js';
import { NULL_FUNCTION } from '../resource/defaults.js';

const LANDSCAPE_ANGLES = [90, 270, -90];
const PORTRAIT_ANGLES = [0, 180];

const isOnMobile = getIsOnMobile();
const isOnIOS = getIsOnIOS();
const isHigherAndroidChrome80 = getIsHigherAndroidChrome80();

const getCurrentAngle = () => {
  if (typeof window === 'undefined') return 0;
  if (typeof window.orientation === 'number') return window.orientation;
  if (window.screen?.orientation?.angle != null) {
    return window.screen.orientation.angle;
  }
  return 0;
};

const lockLandscape = () => {
  window.screen?.orientation?.lock?.('landscape')?.catch?.(NULL_FUNCTION);
};

/**
 * Wire device-orientation → fullscreen behavior to a player-like target.
 * Returns an unsubscribe so the caller can detach listeners on unmount.
 * No-op on desktop, when enterOnRotate is false, or on isHigherAndroidChrome80
 * (Android Chrome 80+ already auto-rotates fullscreen video).
 *
 * @param {object} params
 * @param {{
 *   requestFullscreen: () => Promise<void> | void,
 *   exitFullscreen: () => void,
 *   isFullscreen: () => boolean,
 *   paused: () => boolean,
 * }} params.player
 * @param {{ fullscreen: { enterOnRotate: boolean } }} params.options
 * @returns {() => void} unsubscribe — detaches listeners, idempotent
 *
 * @example
 * const unsubscribe = landscapeFullscreen({
 *   player: control,
 *   options: { fullscreen: { enterOnRotate: true } },
 * });
 * // on unmount:
 * unsubscribe();
 */
const landscapeFullscreen = ({ player, options }) => {
  if (!isOnMobile && !isOnIOS) return NULL_FUNCTION;
  if (!options?.fullscreen?.enterOnRotate) return NULL_FUNCTION;

  const handleRotation = () => {
    if (isHigherAndroidChrome80) return;
    const currentAngle = getCurrentAngle();
    if (LANDSCAPE_ANGLES.includes(currentAngle)) {
      if (player.paused() === false) {
        const requestResult = player.requestFullscreen();
        if (requestResult?.then) {
          requestResult.then(lockLandscape, NULL_FUNCTION);
        } else {
          lockLandscape();
        }
      }
      return;
    }
    if (PORTRAIT_ANGLES.includes(currentAngle) && player.isFullscreen()) {
      player.exitFullscreen();
    }
  };

  const handleFullscreenChange = () => {
    if (player.isFullscreen()) lockLandscape();
  };

  if (isOnIOS) {
    window.addEventListener('orientationchange', handleRotation);
  } else if (window.screen?.orientation) {
    window.screen.orientation.addEventListener('change', handleRotation);
  }
  // iOS Safari fires webkitbeginfullscreen on <video>, not fullscreenchange on document.
  document.addEventListener('fullscreenchange', handleFullscreenChange);

  return () => {
    if (isOnIOS) {
      window.removeEventListener('orientationchange', handleRotation);
    } else if (window.screen?.orientation) {
      window.screen.orientation.removeEventListener('change', handleRotation);
    }
    document.removeEventListener('fullscreenchange', handleFullscreenChange);
  };
};

export default landscapeFullscreen;
